/**
 * Safe Markdown (TMMAPP-0142 §E 24, item 4): a model answer rendered from `parseMarkdown`'s tree as
 * React elements, so no HTML in the text is ever interpreted. Links open in a new tab and show
 * their destination beside the text; an image is its alt text, never a fetched picture. Headings
 * in an answer are bold lines rather than document headings, so an answer never reorders the
 * page's outline. A code block is a raw preview in mono (TMM-T1); inline code stays in the body face
 * on a `surface2` wash, since it may hold an amount (never mono, TMM-B1).
 *
 * The help library (TMMAPP-0142 U2) passes two more: `renderImage`, which shows an article's picture
 * (else an image is its alt text), and `onArticle`, which opens another article from a link to its
 * id (else that link is its text).
 */
import { Fragment, type ReactNode } from 'react';
import {
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { resolveTokens } from '@/theme/theme';
import { parseMarkdown, type MdBlock, type MdInline } from '@/utils/markdown';

interface InlineColors {
  link: string;
  meta: string;
  /** Inline code's wash: the body face on `surface2` (an amount in code is never mono, TMM-B1). */
  codeWash: string;
}

export interface MarkdownImage {
  alt: string;
  /** The raw destination, as written in the source. */
  src: string;
}

interface InlineHooks {
  renderImage?: ((image: MarkdownImage) => ReactNode) | undefined;
  onArticle?: ((id: string) => void) | undefined;
}

function inline(nodes: MdInline[], colors: InlineColors, hooks: InlineHooks = {}): ReactNode {
  const { link: linkColor, meta: metaColor } = colors;
  return nodes.map((node, index) => {
    switch (node.type) {
      case 'text':
        return <Fragment key={index}>{node.text}</Fragment>;
      case 'code':
        return (
          <Box
            key={index}
            component="code"
            data-md-code=""
            sx={{ fontFamily: 'inherit', bgcolor: colors.codeWash, px: 0.5, borderRadius: 0.5 }}
          >
            {node.text}
          </Box>
        );
      case 'strong':
        return (
          <Box key={index} component="strong" sx={{ fontWeight: 600 }}>
            {inline(node.children, colors, hooks)}
          </Box>
        );
      case 'em':
        return <em key={index}>{inline(node.children, colors, hooks)}</em>;
      case 'image':
        return (
          <Fragment key={index}>
            {hooks.renderImage === undefined
              ? node.alt
              : hooks.renderImage({ alt: node.alt, src: node.src })}
          </Fragment>
        );
      case 'article': {
        const label = inline(node.children, colors, hooks);
        const open = hooks.onArticle;
        if (open === undefined) return <Fragment key={index}>{label}</Fragment>;
        return (
          <Link
            key={index}
            component="button"
            type="button"
            data-article-link={node.id}
            onClick={() => open(node.id)}
            sx={{ color: linkColor, verticalAlign: 'baseline', font: 'inherit' }}
          >
            {label}
          </Link>
        );
      }
      case 'break':
        return <br key={index} />;
      case 'link': {
        const label = inline(node.children, colors, hooks);
        const plain =
          node.children.length === 1 && node.children[0]!.type === 'text'
            ? node.children[0]!.text
            : null;
        return (
          <Fragment key={index}>
            <Link
              href={node.href}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: linkColor, overflowWrap: 'anywhere' }}
            >
              {label}
            </Link>
            {plain !== node.href && (
              <Box
                component="span"
                data-link-destination=""
                sx={{ color: metaColor, overflowWrap: 'anywhere', marginInlineStart: 0.5 }}
              >
                ({node.href})
              </Box>
            )}
          </Fragment>
        );
      }
    }
  });
}

export function SafeMarkdown({
  source,
  renderImage,
  onArticle,
}: {
  source: string;
  /** Shows a picture from the source; without it an image is its alt text (no model images). */
  renderImage?: ((image: MarkdownImage) => ReactNode) | undefined;
  /** Opens another help article from a link to its id; without it the link is its text. */
  onArticle?: ((id: string) => void) | undefined;
}) {
  const tokens = resolveTokens(useTheme());
  const colors: InlineColors = { link: tokens.link, meta: tokens.text2, codeWash: tokens.surface2 };
  const hooks: InlineHooks = { renderImage, onArticle };
  const text = (nodes: MdInline[]) => inline(nodes, colors, hooks);

  function block(node: MdBlock, key: number): ReactNode {
    switch (node.type) {
      case 'paragraph':
        return (
          <Typography key={key} variant="body2" component="p" sx={{ m: 0 }}>
            {text(node.children)}
          </Typography>
        );
      case 'heading':
        return (
          <Typography
            key={key}
            variant="body2"
            component="p"
            data-md-heading={node.level}
            sx={{ m: 0, fontWeight: 600, fontSize: node.level <= 2 ? 15 : 14 }}
          >
            {text(node.children)}
          </Typography>
        );
      case 'code':
        return (
          <Box
            key={key}
            component="pre"
            sx={{
              m: 0,
              p: 1.5,
              overflowX: 'auto',
              bgcolor: tokens.surface2,
              border: `1px solid ${tokens.line}`,
              borderRadius: 1,
              color: tokens.text,
            }}
          >
            <Typography variant="mono" component="code" sx={{ whiteSpace: 'pre' }}>
              {node.text}
            </Typography>
          </Box>
        );
      case 'list': {
        const items = node.items.map((item, index) => (
          <Box component="li" key={index} sx={{ '& > p': { m: 0 } }}>
            {item.map(block)}
          </Box>
        ));
        return node.ordered ? (
          <Box
            key={key}
            component="ol"
            start={node.start}
            sx={{ m: 0, paddingInlineStart: 3, typography: 'body2' }}
          >
            {items}
          </Box>
        ) : (
          <Box key={key} component="ul" sx={{ m: 0, paddingInlineStart: 3, typography: 'body2' }}>
            {items}
          </Box>
        );
      }
      case 'quote':
        return (
          <Box
            key={key}
            component="blockquote"
            sx={{
              m: 0,
              paddingInlineStart: 1.5,
              borderInlineStart: `3px solid ${tokens.line}`,
              color: tokens.text2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {node.children.map(block)}
          </Box>
        );
      case 'table':
        return (
          <Box key={key} sx={{ overflowX: 'auto', maxWidth: '100%' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {node.header.map((cell, index) => (
                    <TableCell key={index}>{text(cell)}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {node.rows.map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {row.map((cell, index) => (
                      <TableCell key={index}>{text(cell)}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        );
      case 'rule':
        return (
          <Box
            key={key}
            component="hr"
            sx={{ m: 0, border: 0, borderTop: `1px solid ${tokens.line}` }}
          />
        );
    }
  }

  return (
    <Box
      data-safe-markdown=""
      sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0, color: tokens.text }}
    >
      {parseMarkdown(source).map(block)}
    </Box>
  );
}
